"use client";

import * as React from "react";
import { Button } from "~/components/ui/button";
import { HireRecommendationsStep, InfoTip } from "~/components/marketplace/hire-recommendations-step";
import { RecommendedAgentProfilePreview } from "~/components/marketplace/recommended-agent-profile-preview";
import { TooltipProvider } from "~/components/ui/tooltip";
import type { WebsiteSetupRecommendations } from "~/lib/marketplace/website-suggestions";
import type { HireRecommendationsSelection } from "~/lib/marketplace/hire-recommendations-pricing";

export type HireRecommendationsAgentProfileDraft = {
  agentName: string;
  jobTitle: string;
  profileImageUrl: string;
  persona: string;
};

type HireRecommendationsOverviewStepProps = {
  businessName: string;
  websiteUrl?: string;
  recommendations: WebsiteSetupRecommendations;
  agentProfile: HireRecommendationsAgentProfileDraft;
  onAgentProfileChange: (next: HireRecommendationsAgentProfileDraft) => void;
  selection: HireRecommendationsSelection;
  onSelectionChange: (next: HireRecommendationsSelection) => void;
  isGeneratingPersona?: boolean;
  className?: string;
  /** Hide responsibilities/training/tools block (wizard company step). */
  hideSettings?: boolean;
  /** Override section title (e.g. "Samantha's Profile"). */
  profileSectionTitle?: string;
};

function defaultAgentProfileTitle(agentName: string | undefined): string {
  const name = agentName?.trim();
  if (!name) return "Inbound GPT Agent Profile (Recommended)";
  const possessive = /s$/i.test(name) ? `${name}'` : `${name}'s`;
  return `${possessive} Profile`;
}

export function HireRecommendationsOverviewStep({
  businessName,
  websiteUrl,
  agentProfile,
  onAgentProfileChange,
  selection,
  onSelectionChange,
  isGeneratingPersona = false,
  className,
  hideSettings = false,
  profileSectionTitle,
}: HireRecommendationsOverviewStepProps) {
  const [aboutModalOpen, setAboutModalOpen] = React.useState(false);
  const sectionTitle =
    profileSectionTitle?.trim() || defaultAgentProfileTitle(agentProfile.agentName);

  return (
    <TooltipProvider>
    <div className={className}>
      <section className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-foreground">
              {sectionTitle}
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Review and edit your agent&apos;s name, photo, and persona for {businessName}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => setAboutModalOpen(true)}
            disabled={isGeneratingPersona}
          >
            About
          </Button>
        </div>

        <RecommendedAgentProfilePreview
          businessName={businessName}
          websiteUrl={websiteUrl}
          agentProfile={agentProfile}
          onAgentProfileChange={onAgentProfileChange}
          isGeneratingPersona={isGeneratingPersona}
          hideInlineAbout
          personaModalOpen={aboutModalOpen}
          onPersonaModalOpenChange={setAboutModalOpen}
        />
      </section>

      {!hideSettings ? (
      <section className="mt-6 space-y-4">
        <div>
          <h3 className="inline-flex items-center text-sm font-semibold text-foreground">
            Inbound GPT Agent Settings (Recommended)
            <InfoTip text="Choose skills, usage limits, training level, and integrations. Changes update your live preview after you continue." />
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Adjust skills, usage, and integrations for {businessName}
          </p>
        </div>

        <HireRecommendationsStep
          businessName={businessName}
          selection={selection}
          onChange={onSelectionChange}
          compact
          hideTeamAndLocations
          hideTitle
          showTotal={false}
          showTooltips
        />
      </section>
      ) : null}
    </div>
    </TooltipProvider>
  );
}
